import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import DeleteIcon from '@mui/icons-material/Delete'
import { usDeletePersonPicture, usListPersonPictures } from 'services'
import { type PersonPictureInformation } from 'services/person/us/picture/personGetPicture'
import { AlertDialog, IconButtonWithTooltip, useGlobalMessageHandler } from 'components'

interface PersonPhotoGalleryProps {
  personId: number
  open: boolean
  onClose: () => void
  setImageLink: (imageLink: PersonPictureInformation) => void
}

export const PersonPhotoGallery: React.FC<PersonPhotoGalleryProps> = ({ personId, open, onClose, setImageLink }) => {
  const [pictures, setPictures] = useState<PersonPictureInformation[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [loadFailed, setLoadFailed] = useState<boolean>(false)
  const [pendingDelete, setPendingDelete] = useState<PersonPictureInformation | null>(null)
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false)
  const [deletingImageId, setDeletingImageId] = useState<number | null>(null)
  const { showSuccessMessage, showErrorMessage } = useGlobalMessageHandler()

  useEffect(() => {
    if (!open) return
    setPictures([])
    setLoadFailed(false)
    setLoading(true)
    usListPersonPictures(personId).then((value) => {
      setPictures(value.result ?? [])
    }).catch(() => {
      setLoadFailed(true)
      showErrorMessage('Error getting person photos')
    }).finally(() => { setLoading(false) })
  }, [open, personId])

  const handleRemove = (picture: PersonPictureInformation): void => {
    if (picture.id == null) return
    setDeletingImageId(picture.id)
    usDeletePersonPicture(personId, picture.id).then(value => {
      const survivors = value.result ?? []
      setPictures(survivors)
      showSuccessMessage('Photo removed')
      const newPrimary = survivors.find(p => p.display_order === 1)
      setImageLink(newPrimary ?? {})
    }).catch(() => {
      showErrorMessage('Failed to remove photo')
    }).finally(() => {
      setDeletingImageId(null)
    })
  }

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>Photos</DialogTitle>
        <DialogContent>
          {loading
            ? <CircularProgress />
            : loadFailed
              ? <Typography>Could not load photos.</Typography>
              : pictures.length === 0
                ? <Typography>This person has no photos.</Typography>
                : pictures.map((picture) => (
                  <Box key={picture.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
                    {picture.image != null
                      ? <Box component="img" src={'data:image/jpeg;base64,' + picture.image} alt={`Photo ${String(picture.display_order)}`} sx={{ width: 108, height: 148, objectFit: 'cover' }} />
                      : <Typography>No image data</Typography>
                    }
                    <Typography>{`Display order ${String(picture.display_order)}`}</Typography>
                    <IconButtonWithTooltip
                      label={`Remove photo ${String(picture.display_order)}`}
                      disabled={deletingImageId != null}
                      onClick={() => { setPendingDelete(picture); setConfirmOpen(true) }}
                      icon={<DeleteIcon />}
                    />
                  </Box>
                ))
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
      <AlertDialog
        open={confirmOpen}
        title="Remove photo"
        contentText={`Permanently delete photo ${String(pendingDelete?.display_order)}? It cannot be recovered.`}
        nameButtonConfirm="Remove"
        onButtonClick={() => { if (pendingDelete != null) { handleRemove(pendingDelete) } }}
        onClose={() => { setConfirmOpen(false) }}
      />
    </>
  )
}
